import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import CustomDrawer from "src/components/GeneralComponents/CustomDrawer";
import CustomPopover from "src/components/CustomPopover";
import AddOrUpdateTickets from "./AddOrUpdateTickets";
import {
  DeleteSupportTicket,
  get_list_support_ticket_with_pagination,
  markResolved,
} from "src/DAL/SupportTicket/SupportTicket";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrCard from "src/design-system/components/GrCard";
import GrDataTable from "src/design-system/components/GrDataTable";
import GrTablePagination from "src/design-system/components/GrTablePagination";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrIcon from "src/design-system/components/GrIcon";
import { ticketDate, ticketStatus } from "./ticketHelpers";

const subjectSx = {
  p: 0,
  minWidth: 0,
  maxWidth: "100%",
  justifyContent: "flex-start",
  textAlign: "left",
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.medium,
  color: gr.color.ink,
  borderRadius: gr.radius.chip,
  overflowWrap: "anywhere",
  "&:hover": { color: gr.color.brandStrong, textDecoration: "underline" },
  "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
};

function SupportTicketsTabs({
  tabValue,
  valueData,
  setSolved,
  setOpen,
  setUserList,
  userList,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [editDrawerState, setEditDrawerState] = useState(false);
  // Loading from the first frame (the list loads on mount), so old rows never flash.
  const [isLoading, setIsLoading] = useState(true);
  const [openDelete, setOpenDelete] = useState(false);
  const [editData, setEditData] = useState({});
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [ticketId, setTicketId] = useState("");
  const [openResolved, setOpenResolved] = useState(false);
  const path = window.location.pathname;

  const getTickestsListing = async (value) => {
    setIsLoading(true);
    const formData = new FormData();
    if (!value) {
      formData.append("filter_by", "all");
    } else {
      formData.append("filter_by", value);
    }

    const result = await get_list_support_ticket_with_pagination(
      valueData,
      page,
      rowsPerPage,
      ""
    );
    if (result.code === 200) {
      const data = result.support_ticket.map((ticket) => {
        // One date format and clock for the list and the detail (see ticketDate).
        let timeCreated = ticketDate(ticket.support_ticket_date);
        let resolvedData = ticketDate(ticket.resolve_date);

        return {
          ...ticket,
          timeCreated,
          resolvedData,
        };
      });
      setOpen(result.open_ticket_count);
      setSolved(result.solved_ticket_count);
      setTotalCount(result?.total_count);
      setUserList(data);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerState(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hanleChangePage = (value) => {
    navigate(`/support-ticket/${value._id}`, {
      state: { value, tabValue },
    });
  };

  const handleAgreeDelete = (value) => {
    setTicketId(value._id);
    setOpenDelete(true);
  };

  const handleEdit = (value) => {
    setEditData(value);
    setEditDrawerState(true);
  };

  const handleOpenEditDrawer = () => {
    setEditDrawerState(true);
  };

  const handleMarkResolvedPopup = (value) => {
    setTicketId(value._id);
    setOpenResolved(true);
  };

  const MENU_OPTIONS = [
    {
      label: "Detail",
      icon: icons.file,
      handleClick: hanleChangePage,
    },
    ...(valueData === "open"
      ? [
          {
            label: "Edit",
            icon: icons.edit,
            handleClick: handleEdit,
          },
        ]
      : []),
    {
      label: "Delete",
      icon: icons.deleteTwoTone,
      danger: true,
      handleClick: handleAgreeDelete,
    },
    ...(valueData === "open"
      ? [
          {
            label: "Mark Resolved",
            icon: icons.check,
            handleClick: handleMarkResolvedPopup,
          },
        ]
      : []),
  ];

  const handleDelete = async () => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await DeleteSupportTicket(ticketId);
    if (result.code === 200) {
      getTickestsListing("all");
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleMarkResolved = async () => {
    setOpenResolved(false);
    setIsLoading(true);
    const result = await markResolved(ticketId);
    if (result.code === 200) {
      getTickestsListing("all");
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      navigate(`/support-ticket`);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTickestsListing();
  }, [rowsPerPage, page, path]);

  const columns = [
    {
      key: "reference_number",
      label: "#",
      width: `calc(${gr.space[12]} * 2)`,
      render: (data) => data.reference_number,
    },
    {
      key: "subject",
      label: "Subject",
      primary: true,
      width: "minmax(0, 2fr)",
      render: (data) => {
        const truncatedSubject =
          data.subject.length > 40
            ? data.subject.substring(0, 40) + "..."
            : data.subject;
        return (
          // Opens the ticket, same as Detail in the ⋮ menu.
          <ButtonBase sx={subjectSx} onClick={() => hanleChangePage(data)}>
            {truncatedSubject}
          </ButtonBase>
        );
      },
    },
    {
      key: "timeCreated",
      label: "Created At",
      render: (data) => data.timeCreated,
    },
    valueData === "open"
      ? {
          key: "ticket_status",
          label: "Status",
          render: (data) => {
            const status = ticketStatus(data);
            return (
              <GrStatusPill status={status.pill}>{status.text}</GrStatusPill>
            );
          },
        }
      : {
          key: "resolvedData",
          label: "Resolved Date",
          render: (data) => data.resolvedData,
        },
    {
      key: "action",
      label: "Action",
      action: true,
      showLabel: true,
      align: "right",
      // Room for the "Action" heading; the ⋮ button sits at the right edge under it.
      width: `calc(${gr.control.md} + ${gr.space[6]})`,
      render: (data) => <CustomPopover menu={MENU_OPTIONS} data={data} />,
    },
  ];

  return (
    <Box>
      <GrCard as="div">
        {isLoading ? (
          // While loading: the headings and about the rows' space, so the card does not pop in and jump.
          <>
            <GrDataTable
              ariaLabel="Support tickets"
              columns={columns}
              rows={[]}
            />
            <GrLoader
              sx={{
                minHeight: `calc(${gr.space[12]} * 5)`,
                alignItems: "center",
              }}
            />
          </>
        ) : userList?.length ? (
          <GrDataTable
            ariaLabel="Support tickets"
            columns={columns}
            rows={userList}
            getRowKey={(row) => row._id}
          />
        ) : (
          <GrEmptyState
            compact
            icon={<GrIcon icon={icons.support} />}
            title="No results found"
          />
        )}
        <GrTablePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </GrCard>
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete this Ticket?"}
        handleAgree={handleDelete}
      />
      <CustomConfirmation
        open={openResolved}
        setOpen={setOpenResolved}
        title={"Are you sure you want to mark this ticket as resolved?"}
        handleAgree={handleMarkResolved}
      />
      <CustomDrawer
        isOpenDrawer={editDrawerState}
        onOpenDrawer={handleOpenEditDrawer}
        onCloseDrawer={handleCloseEditDrawer}
        pageTitle="Edit Support Ticket"
        componentToPassDown={
          <AddOrUpdateTickets
            editData={editData}
            formType="EDIT"
            dataList={getTickestsListing}
            onCloseDrawer={handleCloseEditDrawer}
          />
        }
      />
    </Box>
  );
}
export default SupportTicketsTabs;
